// The 7 day auto-confirm promise comes from Q138 in the product spec answers
// and is declared here, client-side, because `packages/contest-engine`
// exports no such constant. The 48 hour naming edit window is the opposite
// case: it is a real engine rule, so it is imported and never redeclared.

import type { typeContestTiebreaker } from "@sportsmet/contest-engine";
import { TIEBREAK_EDIT_WINDOW_MS } from "@sportsmet/contest-engine";

import {
  buildNamingWindowWarning,
  buildNudgedStatusLine,
  CONFIRM_GATE_AUTO_CONFIRM_DEFAULT,
  CONFIRM_GATE_AUTO_CONFIRM_LAST_DAY,
} from "./confirmGateCopy";

export const AUTO_CONFIRM_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

export type typeAutoConfirmTone = "quiet" | "warning";

export type typeAutoConfirmStatus = {
  message: string;
  tone: typeAutoConfirmTone;
  daysUntilAutoConfirm: number;
};

const DAY_MS = 24 * 60 * 60 * 1000;

export function resolveAutoConfirmStatus(input: {
  detectedAtMs: number;
  nudgeSentAtMs: number | null;
  nowMs: number;
}): typeAutoConfirmStatus {
  const msLeft = input.detectedAtMs + AUTO_CONFIRM_WINDOW_MS - input.nowMs;
  const daysUntilAutoConfirm = Math.max(0, Math.floor(msLeft / DAY_MS));

  if (msLeft < DAY_MS) {
    return {
      message: CONFIRM_GATE_AUTO_CONFIRM_LAST_DAY,
      tone: "warning",
      daysUntilAutoConfirm,
    };
  }

  if (input.nudgeSentAtMs !== null) {
    return {
      message: buildNudgedStatusLine({
        daysSinceNudge: Math.max(
          0,
          Math.floor((input.nowMs - input.nudgeSentAtMs) / DAY_MS),
        ),
        daysUntilAutoConfirm,
      }),
      tone: "quiet",
      daysUntilAutoConfirm,
    };
  }

  return {
    message: CONFIRM_GATE_AUTO_CONFIRM_DEFAULT,
    tone: "quiet",
    daysUntilAutoConfirm,
  };
}

export function resolveNamingWindowWarning(input: {
  tiebreaker: typeContestTiebreaker;
  commissionerNamedAtMs: number | null;
  nowMs: number;
}): string | null {
  if (input.tiebreaker !== "commissionerPick") {
    return null;
  }
  if (input.commissionerNamedAtMs === null) {
    return null;
  }
  const msLeft =
    input.commissionerNamedAtMs + TIEBREAK_EDIT_WINDOW_MS - input.nowMs;
  if (msLeft <= 0) {
    return null;
  }
  return buildNamingWindowWarning(Math.ceil(msLeft / (60 * 60 * 1000)));
}
